import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import MainTemplate from "../../containers/MainTemplate";
import "./styles.scss";

interface TimelineItem {
  id: string;
  period: string;
  startYear: number;
  title: string;
  company: string;
  location: string;
  color: string;
  projects?: Array<{
    name: string;
    highlights: string[];
  }>;
  highlights?: string[];
}

const timelineData: TimelineItem[] = [
  {
    id: "samsung",
    period: "06/2024 – Present",
    startYear: 2024,
    title: "Staff Engineer",
    company: "Samsung Ads",
    location: "Mountain View, CA",
    color: "#F8C822",
    highlights: [
      "Lead technical role on Samsung TV+ Ads team (60/40 front-end focus)",
      "Build front-end systems shipping to 70M+ devices with millions of impressions",
      "Spearheaded interactive ad framework delivering $20MM revenue in first year",
      "One of 10 lead maintainers for platform with 600+ contributors, ~100 PRs/week",
      "Architected experimentation API framework handling 10–50k QPS",
      "Led GenAI projects: RAG-powered chat interface and agentic framework for interactive overlays",
      "Regular speaker at company tech talks (60–100 attendees)",
    ],
  },
  {
    id: "evergreen",
    period: "07/2023 – 06/2024",
    startYear: 2023,
    title: "Front End Tech Lead",
    company: "Evergreen Money",
    location: "Miami, FL",
    color: "#E8927C",
    highlights: [
      "Architected web/mobile interfaces for AI-supported investment products and robo-advisors",
      "Implemented WebAuthn/FIDO2 passkeys for secure user sessions",
      "Introduced A/B testing framework driving >50% lift in conversion",
      "Led team of 5 UI engineers across React, Next.js, and Capacitor",
      "Built D3.js data visualizations and animations for multi-platform architecture",
      "Introduced design system to minimize Product-Engineering friction",
    ],
  },
  {
    id: "bp",
    period: "11/2020 – 06/2023",
    startYear: 2020,
    title: "Staff Front End Engineer",
    company: "BP / RTM",
    location: "Florida, USA",
    color: "#9BC8B8",
    projects: [
      {
        name: "Clarity",
        highlights: [
          "Led team of 4 to build NLP transformer data-mining application",
          "Delivered chat interface for ML-assisted reports on commodity datasets",
          "Built apps used in $100M+ trades (React, Electron, Web)",
          "Automated data entry from thousands of daily industry publications",
        ],
      },
      {
        name: "Helix",
        highlights: [
          "Led Agile team of 4 full-stack engineers",
          "Cut development time ~50% by introducing BP's SDK component library",
          "Partnered with Global Head of Design to evolve BP's design system",
        ],
      },
    ],
  },
  {
    id: "deutsche",
    period: "04/2017 – 11/2020",
    startYear: 2017,
    title: "Lead Front End Engineer",
    company: "Deutsche Bank",
    location: "Luxembourg",
    color: "#D4A5A5",
    highlights: [
      "Led team of 5 to build Deutsche Bank Asset Management's first white-label AI robo-advisor",
      "Delivered AI-assisted portfolio app cutting in-person meetings by 50%",
      "Built D3.js interactive data-viz component library for financial products",
      "Maintained mobile banking app with 300k+ DAU",
      "Developed Node.js SDK, REST microservices, GraphQL API, and 50+ component library",
      "Built savings/investment account management (React Native)",
    ],
  },
  {
    id: "ubs",
    period: "02/2016 – 04/2017",
    startYear: 2016,
    title: "Senior Front End Engineer",
    company: "UBS",
    location: "London, UK",
    color: "#A8C5DD",
    highlights: [
      "Developed reactive UI component framework for accessibility design system",
      "Enabled migration of 30+ tools from Adobe Flex to HTML5/JavaScript",
      "Built enterprise Node.js app shell with 45+ components",
      "Drove SDK adoption from handful to dozens of teams org-wide",
    ],
  },
  {
    id: "uix",
    period: "04/2013 – 12/2015",
    startYear: 2013,
    title: "Senior Front End Engineer",
    company: "UIX Limited",
    location: "London, UK",
    color: "#F4B860",
    highlights: [
      "Built responsive websites and web apps for fin-tech startup",
      "Implemented optimization methods: spriting, lazy-loading, custom CSS grid",
      "Designed interactive visualizations for large data sets",
    ],
  },
];

function TimelinePage() {
  gsap.registerPlugin(ScrollTrigger);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (timelineRef.current) {
      const items = timelineRef.current.querySelectorAll(".timeline-item");

      items.forEach((item, index) => {
        // First item is immediately visible
        if (index === 0) {
          gsap.set(item, { opacity: 1, x: 0 });
        } else {
          gsap.fromTo(
            item,
            {
              opacity: 0,
              x: index % 2 === 0 ? -100 : 100,
            },
            {
              opacity: 1,
              x: 0,
              duration: 0.8,
              ease: "power3.out",
              scrollTrigger: {
                trigger: item,
                start: "top 80%",
                end: "top 50%",
                toggleActions: "play none none reverse",
              },
            }
          );
        }
      });
    }
  }, []);

  return (
    <MainTemplate>
      <div className="timeline-page" ref={timelineRef}>
        <h1 className="timeline-title">Career Journey</h1>
        <p className="timeline-subtitle">More than a decade of building exceptional software</p>

        <div className="timeline-container">
          {timelineData.map((item, index) => (
            <div
              key={item.id}
              className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
              style={{ "--accent-color": item.color } as React.CSSProperties}
            >
              <div className="timeline-dot">{item.startYear}</div>
              
              <div className="timeline-content">
                <div className="timeline-header">
                  <h2 className="timeline-company">{item.company}</h2>
                  <span className="timeline-period">{item.period}</span>
                </div>
                <h3 className="timeline-role">{item.title}</h3>
                <span className="timeline-location">{item.location}</span>

                {item.projects ? (
                  <div className="timeline-projects">
                    {item.projects.map((project, idx) => (
                      <div key={idx} className="project-section">
                        <h4 className="project-name">{project.name}</h4>
                        <ul className="timeline-highlights">
                          {project.highlights.map((highlight, hIdx) => (
                            <li key={hIdx}>{highlight}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="timeline-highlights">
                    {item.highlights?.map((highlight, idx) => (
                      <li key={idx}>{highlight}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </MainTemplate>
  );
}

export default TimelinePage;
